import { CollageDesignerConfig
       } from 'collage-designer/collage-designer-config';
import { CollageDesignerConfigParser
       } from 'collage-designer/collage-designer-config-parser';
import { ConfigParser, ConfigParserError } from 'core/config/config-parser';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { Utils } from 'core/utils';
import { BulkAmountConfig, FeatureConfig, FeatureGroupConfig, ImageMediaConfig,
         ImportImage, PriceConfig, ProductConfig, ProductDesignerConfig,
         ProductDetailConfig,
       } from './product-designer-config';

class InvalidFeatureGroupError extends Error
{ }

export class ProductDesignerConfigParser extends ConfigParser
{
  /**
   * @throws ConfigParserError
   */
  private static validateConfig(genericConfig: any): void
  {
    if (genericConfig === undefined) {
      throw new ConfigParserError('content is undefined');
    }
    if (genericConfig.core_products === undefined ||
        genericConfig.core_products.length === 0) {
      throw new ConfigParserError('core_products is undefined');
    }
    if (genericConfig.products === undefined ||
        genericConfig.products.length === 0) {
      throw new ConfigParserError('products is undefined');
    }
  }

  private async getProducts(genericConfig: any): Promise<Array<ProductConfig>>
  {
    let productsConfig: Array<ProductConfig> = [];
    for (let productId of genericConfig.core_products) {
      let productConfig = await this.getProductConfig(genericConfig, productId);

      productsConfig.push(productConfig);
    }
    return productsConfig;
  }

  private static getSelectedProduct(
    genericConfig: any, products: Array<ProductConfig>): string
  {
    return (
      genericConfig.selected_product === undefined
        ? products[0].id
        : genericConfig.selected_product);
  }

  /**
   * @throws ConfigParserError
   */
  private async getProductConfig(
    genericConfig: any, productId: string): Promise<ProductConfig>
  {
    let productConfig = genericConfig.products[productId];
    if (!productConfig) {
      throw new ConfigParserError(
          'configuration for product "' + productId + '" not found');
    }

    let additionalProducts: Array<ProductConfig> = [];
    if (productConfig.additional_products) {
      for (let additionalProductId of productConfig.additional_products) {
        const additionalProduct = await this.getProductConfig(
            genericConfig, additionalProductId);
        if (additionalProduct.inStock) {
          additionalProducts.push(additionalProduct);
        }
      }
    }

    for (let additionalProduct of additionalProducts) {
      additionalProduct.quantity = productConfig.quantity;
    }

    let details: Array<ProductDetailConfig> = [];
    if (productConfig.details) {
      for (let detail of productConfig.details) {
        details.push(<ProductDetailConfig> detail);
      }
    }

    let bulletedDescription: Array<string> = [];
    if (productConfig.bulleted_description) {
      for (let item of productConfig.bulleted_description) {
        bulletedDescription.push(item.value);
      }
    }

    let inStock = typeof productConfig.in_stock === 'boolean'
          ? productConfig.in_stock : true;
    let featureGroups = {};
    try {
      featureGroups = ProductDesignerConfigParser.getFeatureGroups(
          productConfig.feature_groups, productConfig.features);
    }
    catch(InvalidFeatureGroupError) {
      inStock = false;
    }

    return <ProductConfig> {
      id: <string> productId,
      configurationId: productConfig.configuration_id,
      name: productConfig.name,
      shortName: <string> productConfig.short_name,
      extraShortName: productConfig.extra_short_name
          ? <string> productConfig.extra_short_name
          : <string> productConfig.short_name,
      iconURL: ProductDesignerConfigParser.getIconIfProvided(
          productConfig.icons),
      inStock: inStock,
      quantity: productConfig.quantity ? productConfig.quantity : 1,
      price: ProductDesignerConfigParser.getPriceConfig(productConfig.price),
      packagingUnit: productConfig.packaging_unit ?
          productConfig.packaging_unit : 1,
      type: productConfig.type,
      description: <string> productConfig.description,
      bulletedDescription: <Array<string>> bulletedDescription,
      catchphrase: <string> productConfig.catchphrase,
      details: <Array<ProductDetailConfig>> details,
      image: ProductDesignerConfigParser
        .getImageIfProvided(productConfig.media),
      collageDesignerConfigURL:
          <string> productConfig.initial_collagedesigner_config_url,
      collageDesignerConfig: await this
        .tryResolveCollageDesignerConfig(
          productConfig.collagedesigner_config, productId),
      additionalProducts: additionalProducts,
      selectedAdditionalProducts:
          productConfig.selected_additional_products ?
              <Array<string>> productConfig.selected_additional_products :
              <Array<string>> [],
      featureGroups: featureGroups,
      productStoreURL: Utils.optional(productConfig, ['product_store_url']),
      variant: productConfig.variant,
      size: productConfig.size,
    };
  }

  private static getPriceConfig(priceConfig: any): PriceConfig
  {
    return <PriceConfig> {
      regularAmount: Number(priceConfig.regular_amount),
      reducedAmount: priceConfig.reduced_amount
          ? Number(priceConfig.reduced_amount)
          : Number(priceConfig.regular_amount),
      bulkAmounts: priceConfig.bulk_amounts
        ? ProductDesignerConfigParser
            .getBulkAmountConfigs(priceConfig.bulk_amounts)
        : undefined,
    };
  }

  private static getBulkAmountConfigs(
    bulkAmountConfigs: any): Array<BulkAmountConfig>
  {
    let bulkAmounts: Array<BulkAmountConfig> = [];
    bulkAmountConfigs.forEach((config: any) => {
      bulkAmounts.push(<BulkAmountConfig>{
        minQuantity: config.min_quantity,
        regularAmount: Number(config.regular_amount),
        reducedAmount: config.reduced_amount ?
            Number(config.reduced_amount) : Number(config.regular_amount)
      });
    });
    return bulkAmounts;
  }

  private static getIconIfProvided(icons: any): string | undefined
  {
    if (!icons) {
      return undefined;
    }

    const nameIcons = icons.filter(i => i['id'] === 'name-identifier');
    if (nameIcons.length === 0) {
      return undefined;
    }

    return nameIcons[0]['url'];
  }

  private static getImageIfProvided(
    mediaJson: any): ImageMediaConfig | undefined
  {
    if (mediaJson && mediaJson.images && mediaJson.images[0]) {
      return <ImageMediaConfig> {
        altText: mediaJson.images[0].alt_text,
        smallURL: mediaJson.images[0].small_url,
        mediumURL: mediaJson.images[0].medium_url,
        largeURL: mediaJson.images[0].large_url,
      };
    } else {
      return undefined;
    }
  }

  /**
   * @throws ConfigParserError
   * @throws InvalidFeatureGroupError
   */
  private static getFeatureGroups(featureGroupsJson: any, featuresJson: any):
      {[featureGroupId: string]: FeatureGroupConfig}
  {
    let featureGroups: {[featureGroupId: string]: FeatureGroupConfig} = {};
    for (let featureGroupId of Object.keys(featureGroupsJson)) {
      let featureGroupJson = featureGroupsJson[featureGroupId];
      let features: {[featureId: string]: FeatureConfig} = {};
      featureGroupJson.features.forEach((featureId: string) => {
        features[featureId] = ProductDesignerConfigParser.getFeature(
          featuresJson, featureId);
      });

      let selectedFeatureId = undefined;
      if (features[featureGroupJson.selected_feature].inStock) {
        selectedFeatureId = featureGroupJson.selected_feature;
      }
      else {
        for (const featureId of featureGroupJson.features) {
          if (features[featureId].inStock) {
            selectedFeatureId = featureId;
            break;
          }
        }
      }
      if (!selectedFeatureId) {
        throw new InvalidFeatureGroupError('no feature with stock available');
      }
      features[selectedFeatureId].isDefault = true;

      featureGroups[featureGroupId] = <FeatureGroupConfig> {
        id: featureGroupId,
        name: <string> featureGroupJson.name,
        features: features,
        selectedFeature: selectedFeatureId
      };
    }

    return featureGroups;
  }

  /**
   * @throws ConfigParserError
   */
  private static getFeature(featuresJson: any, featureId: string): FeatureConfig
  {
    let featureConfigJson = featuresJson[featureId];
    if (!featureConfigJson) {
      throw new ConfigParserError(
          'configuration for feature "' + featureId + '" not found');
    }

    let featureConfig = <FeatureConfig> {
      id: featureId,
      name: featureConfigJson.name,
      shortName: featureConfigJson.short_name,
      catchphrase: featuresJson.catchphrase,
      price: ProductDesignerConfigParser
        .getPriceConfig(featureConfigJson.price),
      image: ProductDesignerConfigParser
        .getImageIfProvided(featureConfigJson.media),
      isPromoted: featureConfigJson.is_promoted,
      description: featureConfigJson.description,
      inStock: typeof featureConfigJson.in_stock === 'boolean'
          ? featureConfigJson.in_stock : true
    };

    return featureConfig;
  }

  private static getImportImages(genericConfig: any): Array<ImportImage>
  {
    return (
      genericConfig.import_images === undefined
        ? []
        : genericConfig.import_images);
  }

  private async tryResolveCollageDesignerConfig(
    config: any, productId: string): Promise<CollageDesignerConfig | undefined>
  {
    if (! config) {
      return undefined;
    }

    const collageDesignerConfigParser = new CollageDesignerConfigParser(
      config, productId, this.imageStoreService);

    return await collageDesignerConfigParser.getConfig();
  }

  public constructor(genericConfigObject: any,
                     private imageStoreService: ImageStoreService)
  {
    super(genericConfigObject);
  }

  /**
   * @throws ConfigParserError
   */
  public async getConfig(): Promise<ProductDesignerConfig>
  {
    ProductDesignerConfigParser.validateConfig(
      this.genericConfigObject);

    const products = await this.getProducts(this.genericConfigObject);

    return <ProductDesignerConfig> {
      products: products,
      selectedProduct: ProductDesignerConfigParser.getSelectedProduct(
        this.genericConfigObject, products),
      importImages: ProductDesignerConfigParser.getImportImages(
        this.genericConfigObject),
      productUpgradeDialogPopped: false,
    };
  }

}
